import { useTranslation } from '../../../i18n';
import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, Form, InputNumber, Modal, Popconfirm, Radio, Select, Space, Switch, Tag, Typography } from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import CompactTable from './CompactTable';
import { userPath } from '../../../config/paths';
import { ok, runAction } from './utils';

const { Text } = Typography;

// metric → label, unit, default threshold
const METRICS = {
    cpu: ['CPU usage', '%', 90],
    mem: ['Memory usage', '%', 90],
    net_in: ['Inbound bandwidth', 'Mbps', 100],
    net_out: ['Outbound bandwidth', 'Mbps', 100],
    disk_io: ['Disk read/write', 'MB/s', 100],
    traffic: ['Monthly traffic used', '%', 80],
    offline: ['Offline', '', 0],
};
const DURATIONS = [5, 10, 15, 30, 60];

/** Customer alert rules that apply to this VM (VmAlertService). User console only. */
const AlertRulesCard = ({ vmId, api, isMobile, active }) => {
    const { t } = useTranslation();
    const [rules, setRules] = useState([]);
    const [loading, setLoading] = useState(false);
    const [edit, setEdit] = useState(null); // {} = new, rule = editing
    const [saving, setSaving] = useState(false);
    const [form] = Form.useForm();
    const metric = Form.useWatch('metric', form);

    const load = useCallback(async () => {
        if (!api.getAlertRules) return;
        setLoading(true);
        try {
            const res = await api.getAlertRules(vmId);
            if (ok(res)) setRules(res.data?.items || []);
        } finally {
            setLoading(false);
        }
    }, [api, vmId]);

    useEffect(() => { if (active) load(); }, [active, load]);

    if (!api.getAlertRules) return null;

    const open = (r) => {
        setEdit(r || {});
        form.setFieldsValue(r
            ? { metric: r.metric, threshold: r.threshold || undefined, duration_min: r.duration_min, scope: r.vm_id ? 'vm' : 'all' }
            : { metric: 'cpu', threshold: 90, duration_min: 5, scope: 'vm' });
    };

    const save = async (v) => {
        const data = { metric: v.metric, threshold: v.metric === 'offline' ? 0 : v.threshold, duration_min: v.duration_min, vm_id: v.scope === 'vm' ? vmId : '', enabled: 1 };
        setSaving(true);
        const res = await runAction(() => (edit?.id ? api.updateAlertRule(edit.id, data) : api.createAlertRule(data)), t('Saved'), t('Save failed'));
        setSaving(false);
        if (res) { setEdit(null); load(); }
    };

    const toggle = async (r, enabled) => {
        const res = await runAction(() => api.updateAlertRule(r.id, { metric: r.metric, threshold: r.threshold, duration_min: r.duration_min, vm_id: r.vm_id ?? '', enabled: enabled ? 1 : 0 }), t('Saved'), t('Save failed'));
        if (res) load();
    };

    const remove = async (r) => {
        if (await runAction(() => api.deleteAlertRule(r.id), t('Deleted'), t('Deletion failed'))) load();
    };

    const cond = (r) => {
        const [label, unit] = METRICS[r.metric] || [r.metric, ''];
        if (r.metric === 'offline') return `${t(label)} · ${t('{{n}} min', { n: r.duration_min })}`;
        if (r.metric === 'traffic') return `${t(label)} > ${r.threshold}${unit}`;
        return `${t(label)} > ${r.threshold} ${unit} · ${t('{{n}} min', { n: r.duration_min })}`;
    };

    const columns = [
        { title: t('Condition'), key: 'cond', render: (_, r) => <Text>{cond(r)}</Text> },
        { title: t('Applies to'), key: 'scope', render: (_, r) => (r.vm_id ? t('This instance') : <Tag>{t('All my instances')}</Tag>) },
        {
            title: t('Status'),
            key: 'state',
            render: (_, r) => (!r.enabled ? <Tag>{t('Disabled')}</Tag> : (r.firing_vm_ids || []).includes(Number(vmId)) ? <Tag color="error">{t('Alerting')}</Tag> : <Tag color="success">{t('Normal')}</Tag>),
        },
        {
            title: '',
            key: 'actions',
            align: 'right',
            render: (_, r) => (
                <Space size={4}>
                    <Switch size="small" checked={r.enabled} onChange={(v) => toggle(r, v)} />
                    <Button size="small" type="text" icon={<EditOutlined />} onClick={() => open(r)} aria-label={t('Edit')} />
                    <Popconfirm title={t('Delete this rule?')} okButtonProps={{ danger: true }} onConfirm={() => remove(r)}>
                        <Button size="small" type="text" danger icon={<DeleteOutlined />} aria-label={t('Delete')} />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    const unit = (METRICS[metric] || [])[1];
    return (
        <Card
            size="small"
            title={t('Alert rules')}
            extra={(
                <Space size={8}>
                    <Link to={`${userPath('/settings')}?tab=notifications`} style={{ fontSize: 12 }}>{t('Notification channels')}</Link>
                    <Button size="small" icon={<PlusOutlined />} onClick={() => open(null)}>{t('Add rule')}</Button>
                </Space>
            )}
        >
            <CompactTable isMobile={isMobile} columns={columns} dataSource={rules} loading={loading} emptyText={t('No alert rules yet')} />
            <Modal title={edit?.id ? t('Edit rule') : t('Add rule')} open={!!edit} onCancel={() => setEdit(null)} onOk={() => form.submit()} confirmLoading={saving} destroyOnClose>
                <Form form={form} layout="vertical" onFinish={save}
                    onValuesChange={(c) => { if (c.metric) form.setFieldsValue({ threshold: (METRICS[c.metric] || [])[2] || undefined }); }}>
                    <Form.Item name="metric" label={t('Metric')}>
                        <Select options={Object.entries(METRICS).map(([k, [label]]) => ({ value: k, label: t(label) }))} />
                    </Form.Item>
                    <Space wrap size={12} align="start">
                        {metric !== 'offline' && (
                            <Form.Item name="threshold" label={t('Above')} rules={[{ required: true }]}>
                                <InputNumber min={0.01} max={unit === '%' ? 100 : 100000} addonAfter={unit} style={{ width: 160 }} />
                            </Form.Item>
                        )}
                        {metric !== 'traffic' && (
                            <Form.Item name="duration_min" label={metric === 'offline' ? t('No data for') : t('For')}>
                                <Select style={{ width: 120 }} options={DURATIONS.map((d) => ({ value: d, label: t('{{n}} min', { n: d }) }))} />
                            </Form.Item>
                        )}
                    </Space>
                    <Form.Item name="scope" label={t('Applies to')}>
                        <Radio.Group options={[{ value: 'vm', label: t('This instance') }, { value: 'all', label: t('All my instances') }]} />
                    </Form.Item>
                </Form>
            </Modal>
        </Card>
    );
};

export default AlertRulesCard;
